import { getValidAccessToken } from '../Access/services/authService';
import { get, post } from './httpClient';

/** Tope de adjuntos por feedback (fotos + videos). */
export const FEEDBACK_MEDIA_MAX_FILES = 4;

/** Fotos: jpeg/png/webp/gif hasta 8 MB. */
export const FEEDBACK_IMAGE_MAX_BYTES = 8 * 1024 * 1024;

/** Videos: mp4/quicktime/webm hasta 40 MB. */
export const FEEDBACK_VIDEO_MAX_BYTES = 40 * 1024 * 1024;

const FEEDBACK_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
]);

const FEEDBACK_VIDEO_TYPES = new Set([
  'video/mp4',
  'video/quicktime',
  'video/webm',
]);

/** Timeout del PUT a S3 (videos grandes). */
const MEDIA_UPLOAD_TIMEOUT_MS = 180000;

/**
 * El endpoint de feedback acepta access token (no id token).
 * `httpClient` con `auth: false` + header manual evita mezclar claims.
 * El token se renueva si está por vencer (sin reintento ante 401).
 *
 * @returns {Promise<{ Authorization?: string }>}
 */
const accessAuthHeaders = async () => {
  const token = await getValidAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const readApiMessage = (response) => {
  const text = response?.message || response?.error || response?.detail;
  if (typeof text === 'string' && text.trim()) return text.trim();
  if (Array.isArray(response?.detail) && response.detail.length) {
    const first = response.detail[0];
    if (typeof first === 'string') return first;
    if (first?.msg) return String(first.msg);
  }
  return '';
};

/**
 * @param {string} contentType
 * @returns {'image'|'video'|null}
 */
export function getFeedbackMediaKind(contentType) {
  const type = String(contentType || '').trim().toLowerCase();
  if (FEEDBACK_IMAGE_TYPES.has(type)) return 'image';
  if (FEEDBACK_VIDEO_TYPES.has(type)) return 'video';
  return null;
}

/**
 * @param {string} contentType
 * @returns {number|null} Tope en bytes, o null si el tipo no está permitido.
 */
export function getFeedbackMediaMaxBytes(contentType) {
  const kind = getFeedbackMediaKind(contentType);
  if (kind === 'image') return FEEDBACK_IMAGE_MAX_BYTES;
  if (kind === 'video') return FEEDBACK_VIDEO_MAX_BYTES;
  return null;
}

/**
 * Valida tipo y tamaño de un archivo de feedback.
 *
 * @param {File|Blob} file
 * @returns {{ ok: true, contentType: string, kind: 'image'|'video' }
 *   | { ok: false, message: string }}
 */
export function validateFeedbackMediaFile(file) {
  if (!file) {
    return { ok: false, message: 'Elegí un archivo para adjuntar.' };
  }
  const contentType = String(file.type || '').trim().toLowerCase();
  const kind = getFeedbackMediaKind(contentType);
  if (!kind) {
    return {
      ok: false,
      message:
        'Formato no permitido. Fotos: JPEG, PNG, WebP o GIF. Videos: MP4, MOV o WebM.',
    };
  }
  const maxBytes = getFeedbackMediaMaxBytes(contentType);
  const size = Number(file.size) || 0;
  if (size < 1) {
    return { ok: false, message: 'El archivo está vacío.' };
  }
  if (size > maxBytes) {
    const maxMb = maxBytes / (1024 * 1024);
    return {
      ok: false,
      message:
        kind === 'image'
          ? `La foto no puede superar ${maxMb} MB.`
          : `El video no puede superar ${maxMb} MB.`,
    };
  }
  return { ok: true, contentType, kind };
}

/**
 * GET /getfeedbackmediapresignedurl?contentType=&contentLength=.
 * JWT de usuario (access token).
 *
 * @param {{ contentType: string, contentLength: number }} params
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<object>} Envelope httpClient (`data.presignedUrl`, `data.s3Key`, …).
 */
export async function getFeedbackMediaPresignedUrl(
  { contentType, contentLength } = {},
  { signal } = {},
) {
  return get('/getfeedbackmediapresignedurl', {
    auth: false,
    headers: await accessAuthHeaders(),
    params: {
      contentType,
      contentLength,
    },
    signal,
  });
}

/**
 * PUT del archivo a la URL prefirmada. `Content-Type` y tamaño deben coincidir
 * con lo firmado; si no, S3 responde SignatureDoesNotMatch.
 *
 * fetch directo: httpClient serializa a JSON y apunta a API_BASE_URL.
 *
 * @param {{ presignedUrl: string, file: File|Blob, contentType: string }} params
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ ok: boolean, message?: string, _httpStatus: number|null }>}
 */
export async function putFeedbackMediaToS3(
  { presignedUrl, file, contentType } = {},
  { signal } = {},
) {
  if (!presignedUrl || !file || !contentType) {
    return {
      ok: false,
      message: 'Falta la URL de carga o el archivo.',
      _httpStatus: null,
    };
  }

  const controller = new AbortController();
  const onExternalAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', onExternalAbort);
  }
  let timedOut = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, MEDIA_UPLOAD_TIMEOUT_MS);

  try {
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
      signal: controller.signal,
    });
    if (response.ok) {
      return { ok: true, _httpStatus: response.status };
    }
    return {
      ok: false,
      message: 'No se pudo subir el archivo. Probá de nuevo.',
      _httpStatus: response.status,
    };
  } catch (error) {
    if (timedOut) {
      return {
        ok: false,
        message: 'La subida tardó demasiado. Probá con una conexión más estable.',
        _httpStatus: null,
      };
    }
    if (error?.name === 'AbortError') throw error;
    console.error('Feedback media: PUT a S3 falló', error);
    return {
      ok: false,
      message: 'No se pudo subir el archivo.',
      _httpStatus: null,
    };
  } finally {
    clearTimeout(timeoutId);
    if (signal) signal.removeEventListener('abort', onExternalAbort);
  }
}

/**
 * Presign + PUT. Devuelve `{ s3_key, content_type }` listo para createfeedback.
 *
 * @param {File|Blob} file
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ s3_key: string, content_type: string }>}
 * @throws {Error} Si validación, presign o PUT fallan.
 */
export async function uploadFeedbackMediaFile(file, { signal } = {}) {
  const validation = validateFeedbackMediaFile(file);
  if (!validation.ok) {
    throw new Error(validation.message);
  }

  const { contentType } = validation;
  const contentLength = Number(file.size);

  const presigned = await getFeedbackMediaPresignedUrl(
    { contentType, contentLength },
    { signal },
  );

  if (!presigned._httpOk || !presigned.data?.presignedUrl || !presigned.data?.s3Key) {
    throw new Error(
      readApiMessage(presigned) || 'No se pudo preparar la subida del archivo.',
    );
  }

  const {
    presignedUrl,
    s3Key,
    contentType: signedContentType,
  } = presigned.data;

  const putResult = await putFeedbackMediaToS3(
    {
      presignedUrl,
      file,
      contentType: signedContentType || contentType,
    },
    { signal },
  );

  if (!putResult.ok) {
    throw new Error(putResult.message || 'No se pudo subir el archivo.');
  }

  return {
    s3_key: s3Key,
    content_type: signedContentType || contentType,
  };
}

/**
 * Envía feedback del usuario. `media` es opcional: hasta 4 ítems
 * `{ s3_key, content_type }` ya subidos con uploadFeedbackMediaFile.
 * Conserva throw en error HTTP para el modal (rate limit / backend).
 *
 * @param {object} payload
 * @returns {Promise<object>} Envelope httpClient si `_httpOk`.
 * @throws {Error} Si el backend responde error o rate limit.
 */
export const submitFeedback = async (payload) => {
  const endpoint = process.env.REACT_APP_FEEDBACK_ENDPOINT || '/createfeedback';
  const res = await post(endpoint, {
    auth: false,
    headers: await accessAuthHeaders(),
    body: payload,
  });

  if (res._httpOk) return res;

  const backendMessage = readApiMessage(res);
  const normalizedMessage = String(backendMessage).toLowerCase();

  if (res._httpStatus === 429 || normalizedMessage.includes('rate limit exceeded')) {
    throw new Error('Rate limit exceeded: máximo 5 feedbacks por hora.');
  }

  throw new Error(backendMessage || 'No pudimos enviar tu feedback. Intentá nuevamente.');
};
